import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { QuestionForm } from "../..";
import { Button } from "../../../../Application/layout";
import {
  type OtherAnswer,
  type SurveyQuestion as Question,
  type QuestionAnswer,
  type SurveyQuestionAnswer,
  type WithOtherOptions,
} from "../../../types";
import { SurveyQuestion } from "../SurveyQuestion/SurveyQuestion";

export interface Props {
  question: Question;
  answer?: SurveyQuestionAnswer;
  prevQuestion: () => void;
  nextQuestion: () => void;
  submitAnswer: (a: SurveyQuestionAnswer) => void;
  submitLastAnswer: (a: SurveyQuestionAnswer) => void;
  isFirstQuestion: boolean;
  isLastQuestion: boolean;
  questionOrder: number;
  reset?: boolean;
}

export const SurveyQuestionForm: React.FC<Props> = ({
  question,
  answer,
  prevQuestion,
  nextQuestion,
  submitAnswer,
  submitLastAnswer,
  isFirstQuestion,
  isLastQuestion,
  questionOrder,
  reset,
}) => {
  const { t } = useTranslation("assets");
  const [selectedAnswers, setSelectedAnswers] = useState<QuestionAnswer[]>([]);
  const [otherAnswer, setOtherAnswer] = useState<OtherAnswer>(null);

  useEffect(() => {
    if (answer) {
      setSelectedAnswers(answer?.answers);
      setOtherAnswer(answer?.otherText);
    }
  }, [answer]);

  useEffect(() => {
    if (reset) {
      setSelectedAnswers?.([]);
    }
  }, [reset]);

  const onPrevQuestion = useCallback(() => {
    prevQuestion();
    submitAnswer({ answers: selectedAnswers, otherText: otherAnswer });
    setSelectedAnswers([]);
    setOtherAnswer(null);
  }, [otherAnswer, prevQuestion, selectedAnswers, submitAnswer]);

  const onNextQuestion = useCallback(() => {
    nextQuestion();
    const submit = isLastQuestion ? submitLastAnswer : submitAnswer;
    submit({ answers: selectedAnswers, otherText: otherAnswer });
    setSelectedAnswers([]);
    setOtherAnswer(null);
  }, [isLastQuestion, nextQuestion, otherAnswer, selectedAnswers, submitAnswer, submitLastAnswer]);

  const isNextDisabled = (() => {
    const needsUserInput =
      otherAnswer?.checked &&
      (question as WithOtherOptions<any>).otherAnswerOptions.isUserTextFieldRequired &&
      !otherAnswer.value;
    const needsCheckedAnswer = question?.isRequired && !(selectedAnswers.some(a => a) || otherAnswer?.checked);
    return needsUserInput || needsCheckedAnswer;
  })();

  return (
    <QuestionForm
      question={
        <SurveyQuestion
          question={question}
          selectedAnswers={selectedAnswers}
          setSelectedAnswers={setSelectedAnswers}
          otherText={otherAnswer}
          setOtherText={setOtherAnswer}
          questionOrder={questionOrder}
          key={question.id}
        />
      }
      buttons={
        <>
          {!isFirstQuestion && <Button onClick={onPrevQuestion}>{t("assessments.previous")}</Button>}
          <Button onClick={onNextQuestion} disabled={isNextDisabled}>
            {isLastQuestion ? t("assessments.finish") : t("assessments.next")}
          </Button>
        </>
      }
    />
  );
};
